import type { CallDoc } from '../../shared/api/converters.js';

const DAY_MS = 86_400_000;

export interface DayBucket {
  /** `YYYY-MM-DD` in the business timezone. */
  date: string;
  /** Single-letter weekday for the sparkline caption. */
  label: string;
  count: number;
}

export interface DashboardStats {
  /** Real (non-test) calls the assistant handled today. */
  callsToday: number;
  /** Calls today that ended in a booking or a reschedule. */
  bookingsToday: number;
  /** Calls today that were abandoned or failed outright. */
  missedToday: number;
  /** Seven buckets, oldest first, ending today. */
  volume: DayBucket[];
  /** Today's index inside `volume` (always the last one). */
  todayIndex: number;
  /** Today vs. the mean of the six preceding days, as a percentage. `null`
   *  when there's no prior history to compare against. */
  deltaPct: number | null;
}

/** `YYYY-MM-DD` for an instant, as seen in the business's timezone. */
function dayKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function weekdayLetter(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'narrow' }).format(date);
}

/**
 * Turn the last week of call records into the dashboard's KPI strip
 * (design template §KPI). Test calls are excluded throughout — they'd flatter
 * the numbers the owner uses to judge whether the assistant is earning its
 * keep. Pure so the arithmetic is testable without Firestore.
 */
export function summariseCalls(calls: CallDoc[], timezone: string, now: Date = new Date()): DashboardStats {
  const real = calls.filter((c) => !c.test);

  // Seven day buckets ending today, oldest first.
  const volume: DayBucket[] = Array.from({ length: 7 }, (_, i) => {
    const at = new Date(now.getTime() - (6 - i) * DAY_MS);
    return { date: dayKey(at, timezone), label: weekdayLetter(at, timezone), count: 0 };
  });
  const indexByDate = new Map(volume.map((b, i) => [b.date, i]));

  const today = dayKey(now, timezone);
  let callsToday = 0;
  let bookingsToday = 0;
  let missedToday = 0;

  for (const call of real) {
    const key = dayKey(call.startedAt, timezone);
    const bucket = indexByDate.get(key);
    if (bucket !== undefined) volume[bucket]!.count += 1;
    if (key !== today) continue;

    callsToday += 1;
    if (call.outcome === 'booked' || call.outcome === 'rescheduled') bookingsToday += 1;
    // "Missed" is the caller who got nothing: hung up early, or the call broke.
    if (call.outcome === 'abandoned' || call.outcome === 'error') missedToday += 1;
  }

  const priorDays = volume.slice(0, 6);
  const priorTotal = priorDays.reduce((sum, b) => sum + b.count, 0);
  const deltaPct =
    priorTotal === 0 ? null : Math.round(((callsToday - priorTotal / priorDays.length) / (priorTotal / priorDays.length)) * 100);

  return { callsToday, bookingsToday, missedToday, volume, todayIndex: 6, deltaPct };
}
